import React, { useState, useEffect } from 'react';
import { Category } from '../../types';
import { CategoryCreateRequest, CategoryUpdateRequest } from '../../services/api/categoryService';
import {
  TaskyButton,
  TaskyCheckbox,
  TaskyFormGroup,
  TaskyInput,
  TaskyTextarea,
} from '../tasky';

interface CategoryFormProps {
  category?: Category | null;
  onSubmit: (data: CategoryCreateRequest | CategoryUpdateRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#007bff',
    sort_order: 0,
    is_active: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color || '#007bff',
        sort_order: category.sort_order,
        is_active: category.is_active
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#007bff',
        sort_order: 0,
        is_active: true
      });
    }
  }, [category]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'カテゴリ名は必須です';
    } else if (formData.name.length > 255) {
      newErrors.name = 'カテゴリ名は255文字以内で入力してください';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = '説明は1000文字以内で入力してください';
    }

    if (formData.sort_order < 0) {
      newErrors.sort_order = '並び順は0以上の数値を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      color: formData.color,
      sort_order: formData.sort_order,
      is_active: formData.is_active
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <TaskyFormGroup
        label="カテゴリ名"
        required
        htmlFor="name"
        error={errors.name || undefined}
      >
        <TaskyInput
          type="text"
          id="name"
          hasError={Boolean(errors.name)}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="カテゴリ名を入力してください"
          disabled={isLoading}
          maxLength={255}
        />
      </TaskyFormGroup>

      <TaskyFormGroup
        label="説明"
        htmlFor="description"
        error={errors.description || undefined}
        helper={`${formData.description.length}/1000文字`}
      >
        <TaskyTextarea
          id="description"
          hasError={Boolean(errors.description)}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="カテゴリの説明を入力してください（任意）"
          disabled={isLoading}
          rows={3}
          maxLength={1000}
        />
      </TaskyFormGroup>

      <TaskyFormGroup
        label="カラー"
        htmlFor="color"
        helper="カテゴリの表示色を選択してください"
      >
        <div className="flex items-center gap-3">
          <input
            type="color"
            id="color"
            className="h-10 w-14 shrink-0 cursor-pointer rounded-tasky-sm border border-warm-border bg-white p-1 disabled:cursor-not-allowed disabled:bg-gray-100"
            value={formData.color}
            onChange={(e) => handleInputChange('color', e.target.value)}
            disabled={isLoading}
          />
          <TaskyInput
            type="text"
            value={formData.color}
            onChange={(e) => handleInputChange('color', e.target.value)}
            placeholder="#007bff"
            disabled={isLoading}
            pattern="^#[0-9A-Fa-f]{6}$"
            className="font-mono"
          />
        </div>
      </TaskyFormGroup>

      <TaskyFormGroup
        label="並び順"
        htmlFor="sort_order"
        error={errors.sort_order || undefined}
        helper="数値が小さいほど上位に表示されます"
      >
        <TaskyInput
          type="number"
          id="sort_order"
          hasError={Boolean(errors.sort_order)}
          value={formData.sort_order}
          onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
          min="0"
          disabled={isLoading}
        />
      </TaskyFormGroup>

      <TaskyFormGroup helper="無効にするとユーザーには表示されません">
        <TaskyCheckbox
          id="is_active"
          label="有効"
          checked={formData.is_active}
          onChange={(e) => handleInputChange('is_active', e.target.checked)}
          disabled={isLoading}
        />
      </TaskyFormGroup>

      <div className="flex justify-end gap-3 border-t border-warm-border pt-4">
        <TaskyButton
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
        >
          キャンセル
        </TaskyButton>
        <TaskyButton
          type="submit"
          size="sm"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {isLoading ? (category ? '更新中...' : '作成中...') : (category ? '更新' : '作成')}
        </TaskyButton>
      </div>
    </form>
  );
};

export default CategoryForm;
